/**
 * Node — Node model (equation/text)
 * Per PLAN.md models/Node.js and PRD §4.3
 */

export class Node {
    /**
     * Create a new node
     * @param {string} type - 'latex' or 'text'
     * @param {string} content - Node content
     * @param {number} x - X position in world coordinates
     * @param {number} y - Y position in world coordinates
     */
    constructor(type = 'latex', content = '', x = 0, y = 0) {
        this.id = crypto.randomUUID();
        this.type = type;           // 'latex' | 'text'
        this.content = content;
        this.position = { x, y };
        this.size = { width: 0, height: 0 }; // Auto-computed after render
        this.groupId = null;
        this.createdAt = Date.now();
    }

    /**
     * Update node position
     * @param {number} x 
     * @param {number} y 
     */
    moveTo(x, y) {
        this.position.x = x;
        this.position.y = y;
    }

    /**
     * Move node by delta
     * @param {number} dx 
     * @param {number} dy 
     */
    moveBy(dx, dy) {
        this.position.x += dx;
        this.position.y += dy;
    }

    /**
     * Update node size (called after render)
     * @param {number} width 
     * @param {number} height 
     */
    setSize(width, height) {
        this.size.width = width;
        this.size.height = height;
    }

    /**
     * Get node bounds
     * @returns {{x: number, y: number, width: number, height: number}}
     */
    getBounds() {
        return {
            x: this.position.x,
            y: this.position.y,
            width: this.size.width || 100,
            height: this.size.height || 50
        };
    }

    /**
     * Get center point
     * @returns {{x: number, y: number}}
     */
    getCenter() {
        const bounds = this.getBounds();
        return {
            x: bounds.x + bounds.width / 2,
            y: bounds.y + bounds.height / 2
        };
    }

    /**
     * Check if point is inside node
     * @param {number} x 
     * @param {number} y 
     * @returns {boolean}
     */
    containsPoint(x, y) {
        const bounds = this.getBounds();
        return (
            x >= bounds.x &&
            x <= bounds.x + bounds.width &&
            y >= bounds.y &&
            y <= bounds.y + bounds.height
        );
    }

    /**
     * Clone this node
     * @returns {Node}
     */
    clone() {
        const clone = new Node(this.type, this.content, this.position.x + 20, this.position.y + 20);
        clone.size = { ...this.size };
        return clone;
    }

    /**
     * Serialize to JSON
     * @returns {Object}
     */
    toJSON() {
        return {
            id: this.id,
            type: this.type,
            content: this.content,
            position: { ...this.position },
            size: { ...this.size },
            groupId: this.groupId,
            createdAt: this.createdAt
        };
    }

    /**
     * Create node from JSON
     * @param {Object} data 
     * @returns {Node}
     */
    static fromJSON(data) {
        const node = new Node(data.type, data.content, data.position.x, data.position.y);
        node.id = data.id;
        node.size = data.size || { width: 0, height: 0 };
        node.groupId = data.groupId || null;
        node.createdAt = data.createdAt || Date.now();
        return node;
    }
}
